// profileDisplay - localized display names/descriptions for default prompt profiles.
//
// Default profiles (4 base + 10 bound) are provisioned with canonical English
// name/description stored in the DB. The UI recognizes an untouched default by
// exact match against the canonical text and renders the user's locale instead.
// Renamed (custom) profiles display their stored text as-is.

import type { TFunction } from 'i18next';
import {
  BOUND_PROFILE_SPECS,
  BASE_PROFILE_TEXTS,
  PROFILE_BINDINGS,
} from '@aica/prompt-builder';

// canonical English text -> i18n key suffix under promptBuilder.defaultProfiles
const NAME_KEYS = new Map<string, string>();
const DESCRIPTION_KEYS = new Map<string, string>();

for (const binding of PROFILE_BINDINGS) {
  const spec = BOUND_PROFILE_SPECS[binding];
  NAME_KEYS.set(spec.name, `bound.${binding}`);
  DESCRIPTION_KEYS.set(spec.description, `bound.${binding}`);
}
for (const [category, text] of Object.entries(BASE_PROFILE_TEXTS)) {
  NAME_KEYS.set(text.name, `base.${category}`);
  DESCRIPTION_KEYS.set(text.description, `base.${category}`);
}

const I18N_PREFIX = 'promptBuilder:promptBuilder.defaultProfiles';

/** Localized profile name: translation for untouched defaults, stored name otherwise. */
export function getProfileDisplayName(profile: { name: string }, t: TFunction): string {
  const key = NAME_KEYS.get(profile.name);
  return key ? t(`${I18N_PREFIX}.${key}.name`) : profile.name;
}

/** Localized profile description: translation for untouched defaults, stored text otherwise. */
export function getProfileDisplayDescription(
  profile: { description?: string | null },
  t: TFunction
): string | undefined {
  if (!profile.description) return undefined;
  const key = DESCRIPTION_KEYS.get(profile.description);
  return key ? t(`${I18N_PREFIX}.${key}.description`) : profile.description;
}
